/**
 * Upgrade Prompt Component
 * A reusable component to prompt users to upgrade their subscription for premium features
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Sparkles, ArrowRight } from "lucide-react";

interface UpgradePromptProps {
  title: string;
  description: string;
  featureName?: string;
  className?: string;
  showUpgradeButton?: boolean;
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  title,
  description,
  featureName,
  className = "",
  showUpgradeButton = true,
}) => {
  const navigate = useNavigate();

  return (
    <Card
      className={`border-dashed border-2 border-muted-foreground/20 bg-muted/10 ${className}`}
    >
      <CardHeader className="text-center pb-4">
        <div className="mx-auto w-12 h-12 bg-gradient-to-r from-[#40e0d0] to-[#00bcd4] rounded-xl flex items-center justify-center mb-4">
          <Sparkles className="h-6 w-6 text-white" />
        </div>
        <CardTitle className="text-lg font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent className="text-center space-y-4">
        <p className="text-muted-foreground leading-relaxed">{description}</p>
        {featureName && (
          <div className="text-sm text-muted-foreground bg-background/50 rounded-lg p-3 border">
            <strong>Feature:</strong> {featureName}
          </div>
        )}
        {showUpgradeButton && (
          <div className="pt-2">
            <Button
              onClick={() => navigate("/pricing")}
              className="bg-gradient-to-r from-[#40e0d0] to-[#00bcd4] hover:from-[#40e0d0]/80 hover:to-[#00bcd4]/80 text-white shadow-lg"
            >
              Upgrade Plan
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UpgradePrompt;
